import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { companyBiometricDevicesApi } from "@/services/(company)/biometricDevicesApi";
import { subscriptionApi } from "@/services/(company)/subscriptionApi";
import type {
  ISelectAddOnPlanRequest,
  ISetAddOnPackQuantityRequest,
  TAddOnResponse,
  TListAddOnsResponse,
} from "@/types/(company)/addOns.interface";
import type { ADD_ON_KEY_ENUM } from "@/utils/enums/add-ons.enum";

/**
 * Add-ons (Cameras first): the catalog, what the company holds, and buying,
 * changing and cancelling a tier. A purchase raises a Stripe invoice and
 * changes what the camera module allows, so the other slices that show
 * either are refreshed with it.
 */
export const companyAddOnsApi = createApi({
  reducerPath: "companyAddOnsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["AddOns"],
  endpoints: (builder) => {
    // What a change touches outside this slice: the camera module's settings
    // and storage (they depend on the tier) and the subscription's invoices.
    const refreshDependents = async (
      dispatch: (action: unknown) => unknown,
      queryFulfilled: Promise<unknown>,
    ) => {
      try {
        await queryFulfilled;
      } catch {
        return;
      }
      dispatch(
        companyBiometricDevicesApi.util.invalidateTags([
          "Cameras",
          "CameraRecording",
          "CameraStorage",
          // The device limit moves with the Attendance Devices plan.
          "DeviceCapacity",
          "BiometricDevices",
        ]),
      );
      dispatch(
        subscriptionApi.util.invalidateTags([
          "getUpcomingInvoice",
          "getActivity",
        ]),
      );
    };

    return {
      getAddOns: builder.query<TListAddOnsResponse, void>({
        query: () => ({ url: "/company/add-ons", method: "GET" }),
        providesTags: ["AddOns"],
      }),
      subscribeAddOn: builder.mutation<TAddOnResponse, ISelectAddOnPlanRequest>(
        {
          query: ({ addOnKey, planKey }) => ({
            url: `/company/add-ons/${addOnKey}/subscribe`,
            method: "POST",
            body: { planKey },
          }),
          invalidatesTags: ["AddOns"],
          onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
            refreshDependents(dispatch, queryFulfilled),
        },
      ),
      changeAddOnPlan: builder.mutation<
        TAddOnResponse,
        ISelectAddOnPlanRequest
      >({
        query: ({ addOnKey, planKey }) => ({
          url: `/company/add-ons/${addOnKey}/change-plan`,
          method: "POST",
          body: { planKey },
        }),
        invalidatesTags: ["AddOns"],
        onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
          refreshDependents(dispatch, queryFulfilled),
      }),
      /** Buys more of a pack now, or schedules fewer (0 removes it). */
      setAddOnPackQuantity: builder.mutation<
        TAddOnResponse,
        ISetAddOnPackQuantityRequest
      >({
        query: ({ addOnKey, packKey, quantity }) => ({
          url: `/company/add-ons/${addOnKey}/packs/${packKey}`,
          method: "PUT",
          body: { quantity },
        }),
        invalidatesTags: ["AddOns"],
        onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
          refreshDependents(dispatch, queryFulfilled),
      }),
      /** Ends the add-on at the end of the billing period. */
      cancelAddOn: builder.mutation<TAddOnResponse, ADD_ON_KEY_ENUM>({
        query: (addOnKey) => ({
          url: `/company/add-ons/${addOnKey}`,
          method: "DELETE",
        }),
        invalidatesTags: ["AddOns"],
      }),
      /** Takes back a scheduled downgrade or cancellation. */
      undoAddOnChange: builder.mutation<TAddOnResponse, ADD_ON_KEY_ENUM>({
        query: (addOnKey) => ({
          url: `/company/add-ons/${addOnKey}/pending`,
          method: "DELETE",
        }),
        invalidatesTags: ["AddOns"],
      }),
    };
  },
});

export const {
  useGetAddOnsQuery,
  useSubscribeAddOnMutation,
  useChangeAddOnPlanMutation,
  useSetAddOnPackQuantityMutation,
  useCancelAddOnMutation,
  useUndoAddOnChangeMutation,
} = companyAddOnsApi;
